import _ from "lodash";
import React from "react";
import {Link} from "react-router";
import {Box, Typography, IconButton} from "@material-ui/core";
import LinkIcon from "@material-ui/icons/Link";
import {styled} from "styled-components";
import {OutputValue} from "common_components/flowmaster/llm_run/output_cell";
import {Definition, DefinitionConfig} from "./data_assets_create_view";

import {DataAssetReference} from "./configuration_inputs";
import {OrganisationId} from "common/types/organisation";
import {WorkflowInputValue} from "common/flowmaster/types/workflow";
import {DownstreamRun} from "common/flowmaster/types/llm_run";

const Container = styled.div`
  margin-top: 1em;
  border: 1px solid #ddd;
  border-radius: 4px;
`;

const Header = styled.div`
  display: grid;
  grid-template-columns: 150px 1fr 60px;
  padding: 1em;
  background-color: #fff;
  border-bottom: 1px solid #ddd;
  font-weight: 500;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 150px 1fr 100px;
  padding: 1em;
  border-bottom: 1px solid #eee;
  align-items: center;

  &:last-child {
    border-bottom: none;
  }
`;

const Table = styled.div`
  border: 1px solid #ddd;
  border-radius: 4px;
  overflow: hidden;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  background-color: #fff;
  border-bottom: 1px solid #ddd;
  font-weight: 500;
`;

const TableHeaderCell = styled.div`
  padding: 0.25em 0.5em;
  font-weight: bold;
  &:first-child {
    border-right: 1px solid #ddd;
  }
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;

  &:not(:last-child) {
    border-bottom: 1px solid #eee;
  }
`;

const TableKey = styled.div`
  padding: 0.25em 0.5em;
  font-weight: 500;
  border-right: 1px solid #ddd;
`;

const TableValue = styled.div`
  padding: 0.25em 0.5em;
  position: relative;
`;

const LinkCell = styled.div`
  display: flex;
  justify-content: center;
`;

const OutputContainer = styled.div`
  max-height: 10em;
  overflow: auto;
  position: relative;
`;

const ErrorText = styled.div`
  color: red;
`;

const LinkIdentifier = styled.span`
  font-size: 14px;
  font-weight: 600;
  position: relative;
  left: -3px;
  color: rgb(0, 0, 0, 0.54);
`;

interface ConfigurationRunInputsProps {
  downstreamRuns: DownstreamRun[];
  organisationId: OrganisationId;
  selectedProjectId: string;
  selectedDocumentId: string;
  selectedConfig: DefinitionConfig;
  selectedDefinition: Definition;
}

const ConfigurationRunInputs: React.FC<ConfigurationRunInputsProps> = ({
  downstreamRuns,
  organisationId,
  selectedProjectId,
  selectedDocumentId,
  selectedConfig,
  selectedDefinition,
}) => {
  const definitionInputs = selectedDefinition?.inputs?.values || [];
  const configuredInputs = selectedConfig?.config?.inputs?.values || {};

  const allInputs = definitionInputs.reduce((acc, input) => {
    const inputId = String(input.id);

    if (configuredInputs[inputId] !== undefined) {
      acc[inputId] = configuredInputs[inputId];
    } else if (input.has_default && input.default_value !== undefined) {
      acc[inputId] = input.default_value;
    }

    return acc;
  }, {} as Record<string, WorkflowInputValue>);

  const downstreamRunsMap = downstreamRuns.reduce((acc, run) => {
    acc[run.input_id] = run;
    return acc;
  }, {} as Record<string, DownstreamRun>);

  const getInputDisplayName = (
    inputId: string,
    downstreamRun?: DownstreamRun,
  ) => {
    if (downstreamRun?.input_name) {
      return downstreamRun.input_name;
    }

    const inputDef = definitionInputs.find(
      input => String(input.id) === inputId,
    );
    if (inputDef) {
      return inputDef.label || inputDef.name;
    }

    return "Not found";
  };

  const renderOutput = (
    inputValue: WorkflowInputValue,
    downstreamRun?: DownstreamRun,
  ) => {
    const outputKey = (inputValue as DataAssetReference)?.output_key;

    if (!downstreamRun) {
      return (
        <OutputContainer>
          <OutputValue value={inputValue} />
        </OutputContainer>
      );
    }

    if (!downstreamRun.output) {
      return (
        <OutputContainer>
          <OutputValue value="No output available" />
        </OutputContainer>
      );
    }

    if (!outputKey) {
      if (typeof downstreamRun.output === "object") {
        const entries = Object.entries(downstreamRun.output);
        return (
          <Table>
            <TableHeader>
              <TableHeaderCell>Key</TableHeaderCell>
              <TableHeaderCell>Value</TableHeaderCell>
            </TableHeader>
            {entries.map(([key, value]) => (
              <TableRow key={key}>
                <TableKey>{key}</TableKey>
                <TableValue>
                  <OutputValue value={value} />
                </TableValue>
              </TableRow>
            ))}
          </Table>
        );
      }
      return (
        <OutputContainer>
          <OutputValue value={downstreamRun.output} />
        </OutputContainer>
      );
    }

    const value = _.get(downstreamRun.output, outputKey);
    if (value === undefined || value === null) {
      return (
        <OutputContainer>
          <ErrorText>
            <OutputValue value={`No value found for key: ${outputKey}`} />
          </ErrorText>
        </OutputContainer>
      );
    }

    return (
      <OutputContainer>
        <OutputValue value={value} />
      </OutputContainer>
    );
  };

  if (Object.keys(allInputs).length === 0) {
    return (
      <Box p={2} textAlign="center">
        <Typography variant="body1" color="textSecondary">
          No inputs configured.
        </Typography>
      </Box>
    );
  }

  return (
    <Container>
      <Header>
        <div>Key</div>
        <div>Value</div>
        <div></div>
      </Header>

      {Object.entries(allInputs).map(([inputId, inputValue]) => {
        const downstreamRun = downstreamRunsMap[inputId];
        const inputName = getInputDisplayName(inputId, downstreamRun);

        return (
          <Row key={`input-${inputId}`}>
            <div style={{fontWeight: "bold"}}>{inputName}</div>
            {renderOutput(inputValue, downstreamRun)}
            <LinkCell>
              {downstreamRun ? (
                <>
                  <Link
                    to={`/organisation/${organisationId}/llm_run/${downstreamRun.llm_run_id}/detail`}
                    style={{
                      color: "inherit",
                      display: "flex",
                      alignItems: "end",
                      textDecoration: "none",
                    }}
                  >
                    <IconButton size="small">
                      <LinkIcon />
                    </IconButton>
                    <LinkIdentifier style={{}}>L</LinkIdentifier>
                  </Link>
                  <Link
                    to={`/organisation/${organisationId}/data-assets/definition/${
                      downstreamRun.definition_id
                    }/config/${downstreamRun.config_id}/asset/${
                      downstreamRun.llm_run_id
                    }?${
                      selectedProjectId ? `project=${selectedProjectId}&` : ""
                    }${
                      selectedDocumentId
                        ? `document=${selectedDocumentId}&`
                        : ""
                    }view=inputs`}
                    style={{
                      color: "inherit",
                      display: "flex",
                      alignItems: "end",
                      textDecoration: "none",
                    }}
                  >
                    <IconButton size="small">
                      <LinkIcon />
                    </IconButton>
                    <LinkIdentifier>DA</LinkIdentifier>
                  </Link>
                </>
              ) : (
                <div></div>
              )}
            </LinkCell>
          </Row>
        );
      })}
    </Container>
  );
};

export default ConfigurationRunInputs;
